<template>
    <div>
        <div class="container-fluid">
            <div class="dashboard-v2-shell">
                <DashboardHeaderBar
                    :periodLabel="periodLabel"
                    :reportTypeLabel="reportTypeLabel"
                > 
                    <template #actions>
                        <CfdiFiltersBar
                            v-model="filters"
                            :showSearch="false"
                            :showPeriod="false"
                            :showAdvanced="false"
                            :showStatus="false"
                            :showMethod="false"
                            @apply="refreshItems" 
                        />

                        <div class="report-type-wrapper">
                            <select class="report-type-select" :value="filters.reportType" @change="onReportTypeChange">
                                <option value="facturado">Facturado</option>
                                <option value="flujo">Flujo</option>
                            </select>
                        </div>
                    </template>
                </DashboardHeaderBar>

                <div v-if="dashboardError" class="alert alert-warning py-2" role="alert">{{ dashboardError }}</div>

                <DashboardKpiGrid :items="kpiItems" />

                <div class="row gx-3 gy-3 dashboard-v2-charts-row">
                    <div class="col-12 col-xl-8 dashboard-v2-charts-col">
                        <DashboardChartCard
                            class="dashboard-v2-charts-card"
                            title="Ingresos vs gastos por mes"
                            subtitle="Haz clic en una barra para ver el detalle mensual de CFDIs."
                            :series="review_data"
                            :options="incomeExpenseChartOptions"
                            type="bar"
                            :height="340"
                        />
                    </div>

                    <div class="col-12 col-xl-4 dashboard-v2-charts-col">
                        <DashboardChartCard
                            class="dashboard-v2-charts-card"
                            title="Utilidad mensual"
                            subtitle="Utilidad neta mensual calculada como ingresos menos gastos."
                            :series="utilitySeries"
                            :options="utilityChartOptions"
                            type="line"
                            :height="340"
                        />
                    </div>
                </div>

                <div class="row gx-3 gy-3">
                    <div class="col-12 col-lg-6">
                        <DashboardRankList
                            title="Top clientes por ingreso"
                            subtitle="Ranking anual con base en CFDIs del reporte actual."
                            :items="topClientsByIncome"
                        />
                    </div>

                    <div class="col-12 col-lg-6">
                        <DashboardRankList
                            title="Top proveedores por gasto"
                            subtitle="Ranking anual con base en CFDIs del reporte actual."
                            :items="topSuppliersByExpense"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div v-if="drilldownModal.visible" class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
            <div class="modal-dialog modal-xl" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{ drilldownModal.title }}</h5>
                        <button type="button" class="btn-close" aria-label="Close" @click="closeDrilldownModal"></button>
                    </div>
                    <div class="modal-body">
                        <div v-if="drilldownModal.loading" class="text-center py-4">Consultando detalle...</div>
                        <div v-else-if="drilldownModal.error" class="alert alert-danger mb-0" role="alert">{{ drilldownModal.error }}</div>
                        <div v-else class="table-responsive">
                            <table class="table drilldown-table" :class="drilldownTableClass">
                                <thead>
                                    <tr>
                                        <th>Fecha</th>
                                        <th>Metodo</th>
                                        <th>Folio / UUID</th>
                                        <th>{{ drilldownModal.type == 'incomes' ? 'Receptor' : 'Emisor' }}</th>
                                        <th v-if="showPaymentColumns">Folio / UUID (Pago)</th>
                                        <th class="text-end">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-if="!drilldownModal.rows.length">
                                        <td :colspan="tableColspan" class="text-center">No se encontraron registros para este mes.</td>
                                    </tr>
                                    <tr v-for="(row, index) in drilldownModal.rows" :key="index">
                                        <td>{{ formatDate(row.fecha) }}</td>
                                        <td>
                                            <span :class="['method-badge', getMethodBadgeClass(row.metodo_pago)]">
                                                {{ row.metodo_pago || '-' }}
                                            </span>
                                        </td>
                                        <td>{{ row.folio_cfdi || '-' }}<br />
                                            <button
                                                v-if="row.uuid_cfdi"
                                                type="button"
                                                class="uuid-link-btn"
                                                @click="openCfdiPreview(row.uuid_cfdi)"
                                            >{{ formatUuid(row.uuid_cfdi) }}</button>
                                            <span v-else>-</span>
                                            <br />
                                            <button
                                                type="button"
                                                class="btn btn-outline-primary btn-xs"
                                                :disabled="!row.uuid_cfdi"
                                                @click="downloadCfdiXml(row)"
                                            >XML</button>
                                        </td>
                                        <td>{{ row.counterpart_rfc || '-' }}<br />{{ row.counterpart_name || '-' }}</td>
                                        <td v-if="showPaymentColumns">{{ row.folio_pago || '-' }}<br />
                                            <button
                                                v-if="row.uuid_pago"
                                                type="button"
                                                class="uuid-link-btn"
                                                @click="openCfdiPreview(row.uuid_pago)"
                                            >{{ formatUuid(row.uuid_pago) }}</button>
                                            <span v-else>-</span>
                                            <br />
                                            <button
                                                v-if="row.uuid_pago"
                                                type="button"
                                                class="btn btn-outline-secondary btn-xs ms-1"
                                                @click="downloadPagoXml(row)"
                                            >XML PAGO</button>
                                        </td>
                                        <td class="text-end amount-cell" :class="amountAccentClass">
                                            <span
                                                :class="{ 'fx-amount': isDrilldownFx(row) }"
                                                :data-bs-content="getDrilldownFxPopoverContent(row)"
                                                :data-bs-toggle="isDrilldownFx(row) ? 'popover' : null"
                                                data-bs-trigger="click"
                                                data-bs-placement="top"
                                                tabindex="0"
                                            >
                                                {{ formatAmount(row.monto_calculo) }}
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                                <tfoot v-if="drilldownModal.rows.length">
                                    <tr>
                                        <th :colspan="tableColspan - 1" class="text-end">Total</th>
                                        <th class="text-end amount-cell" :class="amountAccentClass">{{ formatAmount(drilldownTotal) }}</th>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-light" @click="closeDrilldownModal">Cerrar</button>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="drilldownModal.visible" class="modal-backdrop fade show"></div>

        <CfdiPreviewModal
            :visible="cfdiPreviewModal.visible"
            :uuid="cfdiPreviewModal.uuid"
            :rfc="rfc"
            @close="closeCfdiPreview"
        />

        <OnboardingFlowModal
            :show="onboarding.show"
            :steps="onboarding.steps"
            :currentIndex="onboarding.currentIndex"
            @close="closeOnboarding"
            @previous="goOnboardingPrevious"
            @secondary="onOnboardingSecondary"
            @primary="onOnboardingPrimary"
            @efirma-registered="onOnboardingEfirmaRegistered"
        />

        <InitialDownloadsModal
            :visible="initialDownloadsModal.visible"
            :rfc="initialDownloadsModal.rfc"
            @close="closeInitialDownloadsModal"
        />
    </div>
</template>

<script>
import CfdiFiltersBar from "@/components/cfdis/CfdiFiltersBar.vue";
import CfdiPreviewModal from "@/components/cfdis/CfdiPreviewModal.vue";
import OnboardingFlowModal from "@/components/onboarding/OnboardingFlowModal.vue";
import InitialDownloadsModal from "@/components/global/InitialDownloadsModal.vue";
import DashboardHeaderBar from "@/components/dashboard/v2/DashboardHeaderBar.vue";
import DashboardKpiGrid from "@/components/dashboard/v2/DashboardKpiGrid.vue";
import DashboardChartCard from "@/components/dashboard/v2/DashboardChartCard.vue";
import DashboardRankList from "@/components/dashboard/v2/DashboardRankList.vue";

import ws from '@/services/dashboard';
import onboardingService from '@/services/onboarding';
import { pushGtmEvent } from '@/services/gtm';
import moment from "moment";
import { Popover } from 'bootstrap';
import { getGlobalPeriod } from '@/services/globalPeriod';
import { loadingShow, loadingHide } from "@/plugins/loadingControls";

export default {
    components: {
        CfdiFiltersBar,
        CfdiPreviewModal,
        OnboardingFlowModal,
        InitialDownloadsModal,
        DashboardHeaderBar,
        DashboardKpiGrid,
        DashboardChartCard,
        DashboardRankList,
    },
    data() {
        return {
            rfc: '',
            period: '',
            dashboardError: '',
            filters: {
                rfc: '',
                nature: '',
                group: '',
                period: "",
                search: "",
                status: "1",
                method: "ALL",
                reportType: "facturado",
                advanced: [],
            },
            metrics: {
                incomes: 0,
                total_incomes: 0,
                expenses: 0,
                total_expenses: 0,
            },
            review_data: [],
            topClients: [],
            topSuppliers: [],
            drilldownModal: {
                visible: false,
                loading: false,
                error: '',
                title: '',
                type: '',
                reportType: 'facturado',
                rows: [],
            },
            cfdiPreviewModal: {
                visible: false,
                uuid: '',
            },
            onboarding: {
                ready: false,
                show: false,
                flow: null,
                steps: [],
                currentIndex: 0,
                status: 'not_started',
            },
            initialDownloadsModal: {
                visible: false,
                rfc: '',
            },
            monthLabels: ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"],
            monthLabelsLong: ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"],
        };
    },
    computed: {
        selectedYear() {
            const period = String(this.filters.period || moment().format('YYYY'));
            return period.split('-')[0];
        },
        reportTypeLabel() {
            return this.filters.reportType === 'flujo' ? 'Flujo' : 'Facturado';
        },
        periodLabel() {
            const period = String(this.filters.period || '').trim();
            if (!period) return 'Sin periodo';

            if (period.includes('-')) {
                const [year, month] = period.split('-');
                const monthIndex = Math.max(0, Number(month || 1) - 1);
                return `${this.monthLabelsLong[monthIndex]} ${year}`;
            }

            return `Ejercicio ${period}`;
        },
        kpiItems() {
            const periodProfit = Number(this.metrics.incomes || 0) - Number(this.metrics.expenses || 0);
            const yearProfit = Number(this.metrics.total_incomes || 0) - Number(this.metrics.total_expenses || 0);

            return [
                {
                    key: 'income',
                    label: 'Ingresos del periodo',
                    value: Number(this.metrics.incomes || 0),
                    meta: 'Facturacion y flujo de cobros',
                    badge: this.reportTypeLabel,
                    tone: 'income',
                },
                {
                    key: 'expense',
                    label: 'Gastos del periodo',
                    value: Number(this.metrics.expenses || 0),
                    meta: 'Compras, egresos y pagos',
                    badge: this.reportTypeLabel,
                    tone: 'expense',
                },
                {
                    key: 'profit',
                    label: 'Utilidad del periodo',
                    value: periodProfit,
                    meta: 'Ingresos menos gastos del periodo',
                    badge: 'Neta',
                    tone: 'profit',
                },
                {
                    key: 'year_profit',
                    label: 'Utilidad acumulada',
                    value: yearProfit,
                    meta: `Balance anual ${this.selectedYear}`,
                    badge: 'YTD',
                    tone: 'balance',
                },
            ];
        },
        topClientsByIncome() {
            return (this.topClients || [])
                .slice()
                .sort((left, right) => this.parseRankingTotal(right?.total) - this.parseRankingTotal(left?.total))
                .slice(0, 5);
        },
        topSuppliersByExpense() {
            return (this.topSuppliers || [])
                .slice()
                .sort((left, right) => this.parseRankingTotal(right?.total) - this.parseRankingTotal(left?.total))
                .slice(0, 5);
        },
        utilitySeries() {
            const incomeData = this.review_data?.[0]?.data || [];
            const expenseData = this.review_data?.[1]?.data || [];

            const utility = this.monthLabels.map((_, index) => {
                const income = Number(incomeData[index] || 0);
                const expense = Number(expenseData[index] || 0);
                return Number((income - expense).toFixed(2));
            });

            return [{ name: 'Utilidad', data: utility }];
        },
        incomeExpenseChartOptions() {
            return {
                chart: {
                    toolbar: { show: false },
                    events: {
                        dataPointSelection: (event, chartContext, config) => {
                            if (config.dataPointIndex < 0 || config.seriesIndex < 0) return;

                            this.openReviewGraphDrilldown({
                                monthIndex: config.dataPointIndex,
                                seriesIndex: config.seriesIndex,
                                seriesName: this.review_data?.[config.seriesIndex]?.name || '',
                            });
                        }
                    }
                },
                dataLabels: {
                    enabled: false,
                },
                plotOptions: {
                    bar: {
                        borderRadius: 6,
                        columnWidth: '48%',
                    }
                },
                xaxis: {
                    categories: this.monthLabels,
                    labels: { style: { colors: '#4f6775' } },
                },
                yaxis: {
                    labels: {
                        formatter: (value) => this.formatCompactCurrency(value),
                    },
                },
                colors: ['#7366ff', '#f73164'],
                legend: {
                    position: 'top',
                },
                tooltip: {
                    y: {
                        formatter: (value) => this.formatAmount(value),
                    },
                },
                grid: {
                    borderColor: '#e3edf2',
                    strokeDashArray: 4,
                },
            };
        },
        utilityChartOptions() {
            return {
                chart: {
                    toolbar: { show: false },
                    dropShadow: {
                        enabled: true,
                        top: 6,
                        left: 0,
                        blur: 10,
                        color: '#7366ff',
                        opacity: 0.22,
                    },
                },
                dataLabels: {
                    enabled: false,
                },
                stroke: {
                    width: 3,
                    curve: 'smooth',
                },
                markers: {
                    size: 3,
                },
                xaxis: {
                    categories: this.monthLabels,
                },
                yaxis: {
                    labels: {
                        formatter: (value) => this.formatCompactCurrency(value),
                    },
                },
                fill: {
                    type: 'gradient',
                    gradient: {
                        shadeIntensity: 1,
                        opacityFrom: 0.4,
                        opacityTo: 0.05,
                        stops: [0, 90, 100],
                    },
                },
                colors: ['#7366ff'],
                tooltip: {
                    y: {
                        formatter: (value) => this.formatAmount(value),
                    }
                },
                grid: {
                    borderColor: '#e3edf2',
                    strokeDashArray: 4,
                },
            };
        },
        showPaymentColumns() {
            if (this.drilldownModal.reportType === 'flujo') {
                return true;
            }

            return (this.drilldownModal.rows || []).some((row) => !!row?.uuid_pago);
        },
        tableColspan() {
            return this.showPaymentColumns ? 6 : 5;
        },
        drilldownTotal() {
            return (this.drilldownModal.rows || []).reduce((sum, row) => sum + Number(row?.monto_calculo || 0), 0);
        },
        amountAccentClass() {
            return this.drilldownModal.type === 'expenses' ? 'amount-expense' : 'amount-income';
        },
        drilldownTableClass() {
            return this.drilldownModal.type === 'expenses' ? 'drilldown-expense' : 'drilldown-income';
        }
    },
    mounted: async function () {
        this.rfc = String(localStorage.getItem('rfc') || '').trim();
        const hasDashboardRfc = this.ensureDashboardRfc();

        this.period = getGlobalPeriod();

        this.filters.rfc = this.rfc;
        this.filters.period = this.period;

        if (hasDashboardRfc) {
            this.refreshItems();
        }

        this.loadOnboarding({ autoOpen: true });
        this.initPopovers();
    },
    updated() {
        this.initPopovers();
    },
    beforeUnmount() {
        this.disposePopovers();
    },
    beforeDestroy() {
        this.disposePopovers();
    },
    methods: {
        parseRankingTotal(value) {
            if (typeof value === 'number') {
                return Number.isFinite(value) ? value : 0;
            }

            const normalized = String(value ?? '').replace(/[^0-9.-]/g, '');
            const parsed = Number(normalized);
            return Number.isFinite(parsed) ? parsed : 0;
        },
        parseStoredRfcs() {
            const raw = localStorage.getItem('rfcs');

            if (!raw || raw === 'undefined') {
                return [];
            }

            try {
                const parsed = JSON.parse(raw);
                if (!Array.isArray(parsed)) {
                    return [];
                }

                return parsed
                    .map((item) => {
                        if (typeof item === 'string') {
                            return { rfc: String(item || '').trim(), name: '' };
                        }

                        if (item && typeof item === 'object') {
                            return {
                                rfc: String(item.rfc || item.RFC || '').trim(),
                                name: String(item.name || item.fiscal_name || item.razon_social || '').trim(),
                            };
                        }

                        return { rfc: '', name: '' };
                    })
                    .filter((item) => item.rfc);
            } catch (error) {
                return [];
            }
        },
        ensureDashboardRfc() {
            if (this.rfc) {
                return true;
            }

            const rfcs = this.parseStoredRfcs();
            if (!rfcs.length) {
                return false;
            }

            const fallbackRfc = String(rfcs[0]?.rfc || '').trim();
            if (!fallbackRfc) {
                return false;
            }

            this.rfc = fallbackRfc;
            localStorage.setItem('rfc', fallbackRfc);
            localStorage.setItem('fiscal_name', String(rfcs[0]?.name || ''));

            return true;
        },
        formatCompactCurrency(value) {
            const abs = Math.abs(Number(value || 0));

            if (abs >= 1000000) {
                return `${(Number(value || 0) / 1000000).toFixed(1)}M`;
            }

            if (abs >= 1000) {
                return `${(Number(value || 0) / 1000).toFixed(0)}k`;
            }

            return Number(value || 0).toFixed(0);
        },
        initPopovers() {
            this.$nextTick(() => {
                if (!this.$el) {
                    return;
                }

                const elements = this.$el.querySelectorAll('[data-bs-toggle="popover"]');
                elements.forEach((element) => {
                    let instance = Popover.getInstance(element);
                    if (!instance) {
                        instance = new Popover(element, {
                            trigger: 'click',
                            html: false,
                            container: 'body',
                            customClass: 'fx-popover',
                        });
                    }

                    if (!element.dataset.fxPopoverBound) {
                        element.addEventListener('show.bs.popover', () => {
                            this.closeOtherPopovers(element);
                        });
                        element.dataset.fxPopoverBound = '1';
                    }
                });
            });
        },
        closeOtherPopovers(currentElement) {
            if (!this.$el) {
                return;
            }

            const elements = this.$el.querySelectorAll('[data-bs-toggle="popover"]');
            elements.forEach((element) => {
                if (element === currentElement) {
                    return;
                }

                const instance = Popover.getInstance(element);
                if (instance) {
                    instance.hide();
                }
            });
        },
        disposePopovers() {
            if (!this.$el) {
                return;
            }

            const elements = this.$el.querySelectorAll('[data-bs-toggle="popover"]');
            elements.forEach((element) => {
                const instance = Popover.getInstance(element);
                if (instance) {
                    instance.dispose();
                }
            });
        },
        onReportTypeChange(event) {
            this.changeReportType(event.target.value);
        },
        changeReportType(reportType) {
            if (this.filters.reportType === reportType) {
                return;
            }

            this.filters.reportType = reportType;
            this.refreshItems();
        },
        async refreshItems() {
            this.period = this.filters.period;
            this.dashboardError = '';

            loadingShow({ text: 'Cargando dashboard...' });

            try {
                const requestConfig = { skipLoading: true };

                const [metricsResult, reviewResult, rankingsResult] = await Promise.allSettled([
                    ws.getDashboardMetrics(this.rfc, this.filters, requestConfig),
                    ws.getReviewGraph(this.rfc, this.filters, requestConfig),
                    ws.getDashboardRankings(this.rfc, this.filters, requestConfig),
                ]);

                let hasErrors = false;

                if (metricsResult.status === 'fulfilled' && metricsResult.value?.type === 'success') {
                    this.metrics.incomes = metricsResult.value.data.incomes;
                    this.metrics.total_incomes = metricsResult.value.data.total_incomes;
                    this.metrics.expenses = metricsResult.value.data.expenses;
                    this.metrics.total_expenses = metricsResult.value.data.total_expenses;
                } else {
                    hasErrors = true;
                }

                if (reviewResult.status === 'fulfilled' && reviewResult.value?.type === 'success') {
                    this.review_data = reviewResult.value.data;
                } else {
                    hasErrors = true;
                }

                if (rankingsResult.status === 'fulfilled' && rankingsResult.value?.type === 'success') {
                    this.topClients = rankingsResult.value?.data?.top_clients || [];
                    this.topSuppliers = rankingsResult.value?.data?.top_suppliers || [];
                } else {
                    this.topClients = [];
                    this.topSuppliers = [];
                    hasErrors = true;
                }

                if (hasErrors) {
                    this.dashboardError = 'No se pudo cargar toda la informacion del dashboard. Intenta nuevamente.';
                }
            } finally {
                loadingHide();
            }
        },
        closeDrilldownModal() {
            this.drilldownModal.visible = false;
            this.drilldownModal.loading = false;
            this.drilldownModal.error = '';
            this.drilldownModal.title = '';
            this.drilldownModal.type = '';
            this.drilldownModal.reportType = 'facturado';
            this.drilldownModal.rows = [];
        },
        formatAmount(value) {
            const number = Number(value || 0);
            return new Intl.NumberFormat('es-MX', {
                style: 'currency',
                currency: 'MXN',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(number);
        },
        normalizeCurrency(value) {
            const normalized = String(value || 'MXN').trim().toUpperCase();
            return normalized || 'MXN';
        },
        formatAmountByCurrency(amount, currencyCode) {
            const number = Number(amount || 0);
            const currency = this.normalizeCurrency(currencyCode);

            try {
                return new Intl.NumberFormat('es-MX', {
                    style: 'currency',
                    currency,
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                }).format(number);
            }
            catch (error) {
                return `${currency} ${number.toFixed(2)}`;
            }
        },
        isDrilldownFx(row) {
            const currency = this.normalizeCurrency(row?.moneda_original || 'MXN');
            return Boolean(row?.fx_converted && currency !== 'MXN');
        },
        getDrilldownFxPopoverContent(row) {
            if (!this.isDrilldownFx(row)) return '';

            const currency = this.normalizeCurrency(row?.moneda_original || 'MXN');
            const original = Number(row?.monto_original ?? row?.monto_calculo ?? 0);
            const exchangeRate = Number(row?.tipo_cambio_aplicado || 1);

            return `Convertido desde ${this.formatAmountByCurrency(original, currency)} con TC ${exchangeRate.toFixed(6)}.`;
        },
        formatDate(value) {
            if (!value) return '-';
            return moment(value).format('DD/MM/YYYY');
        },
        formatUuid(value) {
            if (!value) return '';
            return String(value).toUpperCase();
        },
        async downloadXmlByUuid(uuid) {
            const normalizedUuid = String(uuid || '').toUpperCase();

            if (!normalizedUuid) {
                return;
            }

            try {
                const response = await ws.getDashboardCfdiXml(this.rfc, normalizedUuid, { skipLoading: true });
                const blob = response instanceof Blob
                    ? response
                    : new Blob([response], { type: 'application/xml;charset=utf-8' });

                const blobUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = `${normalizedUuid}.xml`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);
            } catch (error) {
                this.drilldownModal.error = 'No fue posible descargar el XML seleccionado.';
            }
        },
        async downloadCfdiXml(row) {
            await this.downloadXmlByUuid(row?.uuid_cfdi);
        },
        async downloadPagoXml(row) {
            await this.downloadXmlByUuid(row?.uuid_pago);
        },
        openCfdiPreview(uuid) {
            const normalized = String(uuid || '').trim().toUpperCase();
            if (!normalized) return;
            this.cfdiPreviewModal.uuid = normalized;
            this.cfdiPreviewModal.visible = true;
        },
        getCurrentOnboardingStep() {
            if (!Array.isArray(this.onboarding.steps) || !this.onboarding.steps.length) {
                return null;
            }

            const maxIndex = this.onboarding.steps.length - 1;
            const safeIndex = Math.max(0, Math.min(this.onboarding.currentIndex, maxIndex));
            return this.onboarding.steps[safeIndex] || null;
        },
        resolveOnboardingStepIndexByKey(stepKey) {
            const normalizedKey = String(stepKey || '').trim();
            if (!normalizedKey || !Array.isArray(this.onboarding.steps)) {
                return 0;
            }

            const foundIndex = this.onboarding.steps.findIndex((item) => String(item?.key || '') === normalizedKey);
            return foundIndex >= 0 ? foundIndex : 0;
        },
        async loadOnboarding({ autoOpen = false } = {}) {
            try {
                const response = await onboardingService.getCurrent();
                if (!response || response.type !== 'success' || !response.data?.flow) {
                    this.onboarding.ready = true;
                    return;
                }

                this.onboarding.flow = response.data.flow;
                this.onboarding.steps = Array.isArray(response.data.flow?.steps) ? response.data.flow.steps : [];
                this.onboarding.status = String(response.data.status?.status || 'not_started');

                const currentStepKey = String(response.data.status?.current_step_key || '').trim();
                this.onboarding.currentIndex = this.resolveOnboardingStepIndexByKey(currentStepKey);
                this.onboarding.ready = true;

                if (autoOpen && response.data.should_show_auto && this.onboarding.steps.length) {
                    await this.openOnboarding('auto');
                }
            } catch (error) {
                this.onboarding.ready = true;
            }
        },
        async openOnboarding(source = 'manual') {
            if (!this.onboarding.steps.length) {
                return;
            }

            this.onboarding.show = true;

            const currentStep = this.getCurrentOnboardingStep();
            await this.trackOnboardingEvent('onboarding_started', {
                step_key: currentStep?.key || '',
                source,
            });
            await this.trackCurrentOnboardingStepViewed(source);
        },
        closeOnboarding() {
            this.onboarding.show = false;
        },
        async trackOnboardingEvent(eventName, payload = {}) {
            const currentStep = this.getCurrentOnboardingStep();

            pushGtmEvent(eventName, {
                onboarding_flow_key: String(this.onboarding.flow?.key || ''),
                onboarding_flow_version: Number(this.onboarding.flow?.version || 0),
                step_key: String(payload.step_key || currentStep?.key || ''),
                source: String(payload.source || ''),
            });

            try {
                const shouldSendDontShowAgain = eventName === 'onboarding_skipped';
                await onboardingService.trackEvent({
                    event_name: eventName,
                    step_key: String(payload.step_key || currentStep?.key || ''),
                    ...(shouldSendDontShowAgain ? { dont_show_again: true } : {}),
                });
            } catch (error) {
            }
        },
        async trackCurrentOnboardingStepViewed(source = 'manual') {
            const currentStep = this.getCurrentOnboardingStep();
            if (!currentStep) {
                return;
            }

            await this.trackOnboardingEvent('onboarding_step_viewed', {
                step_key: currentStep.key,
                source,
            });
        },
        async goOnboardingPrevious() {
            if (!this.onboarding.steps.length || this.onboarding.currentIndex <= 0) {
                return;
            }

            this.onboarding.currentIndex -= 1;
            await this.trackCurrentOnboardingStepViewed('previous');
        },
        async goOnboardingNext() {
            if (!this.onboarding.steps.length) {
                return;
            }

            const lastIndex = this.onboarding.steps.length - 1;
            if (this.onboarding.currentIndex >= lastIndex) {
                await this.completeOnboardingAndGoToEfirma();
                return;
            }

            this.onboarding.currentIndex += 1;
            await this.trackCurrentOnboardingStepViewed('next');
        },
        async skipOnboarding() {
            await this.trackOnboardingEvent('onboarding_skipped', {
                source: 'skip',
            });

            this.onboarding.status = 'skipped';
            this.closeOnboarding();
        },
        async completeOnboardingAndGoToEfirma() {
            await this.trackOnboardingEvent('onboarding_completed', {
                source: 'complete',
            });

            this.onboarding.status = 'completed';
            this.closeOnboarding();

            this.$router.push({
                name: 'settings',
                query: {
                    section: 'rfcs',
                    onboarding: 'efirma',
                },
            });
        },
        syncRfcsToLocalStorage(rfcList = []) {
            const normalized = (Array.isArray(rfcList) ? rfcList : [])
                .map((item) => {
                    if (typeof item === 'string') {
                        return { rfc: String(item || '').trim(), name: '' };
                    }

                    return {
                        rfc: String(item?.rfc || item?.RFC || '').trim(),
                        name: String(item?.name || item?.fiscal_name || item?.razon_social || '').trim(),
                    };
                })
                .filter((item) => item.rfc);

            localStorage.setItem('rfcs', JSON.stringify(normalized));

            if (normalized.length) {
                localStorage.setItem('rfc', normalized[0].rfc);
                localStorage.setItem('fiscal_name', normalized[0].name || '');
                this.rfc = normalized[0].rfc;
                this.filters.rfc = normalized[0].rfc;
            }

            window.dispatchEvent(new CustomEvent('rfcs-storage-updated'));
        },
        openInitialDownloadsModal(rfc) {
            this.initialDownloadsModal.rfc = String(rfc || localStorage.getItem('rfc') || '').trim().toUpperCase();
            this.initialDownloadsModal.visible = true;
        },
        closeInitialDownloadsModal() {
            this.initialDownloadsModal.visible = false;
            this.initialDownloadsModal.rfc = '';
        },
        async onOnboardingEfirmaRegistered(payload = {}) {
            const rfcList = Array.isArray(payload)
                ? payload
                : (Array.isArray(payload?.rfcs) ? payload.rfcs : []);
            const registeredRfc = String(payload?.registeredRfc || '').trim().toUpperCase();

            this.syncRfcsToLocalStorage(rfcList);

            await this.trackOnboardingEvent('efirma_uploaded', {
                step_key: 'upload_efirma',
                source: 'onboarding',
            });

            await this.trackOnboardingEvent('onboarding_completed', {
                source: 'onboarding_efirma_inline',
            });

            this.onboarding.status = 'completed';
            this.closeOnboarding();
            this.openInitialDownloadsModal(registeredRfc);
            this.refreshItems();
        },
        async onOnboardingPrimary() {
            const currentStep = this.getCurrentOnboardingStep();
            const action = String(currentStep?.primary_cta_action || 'next_step').trim();

            if (action === 'skip_onboarding') {
                await this.skipOnboarding();
                return;
            }

            if (action === 'go_to_efirma_upload') {
                await this.completeOnboardingAndGoToEfirma();
                return;
            }

            if (action === 'prev_step') {
                await this.goOnboardingPrevious();
                return;
            }

            await this.goOnboardingNext();
        },
        async onOnboardingSecondary() {
            const currentStep = this.getCurrentOnboardingStep();
            const action = String(currentStep?.secondary_cta_action || '').trim();

            if (!action) {
                return;
            }

            if (action === 'skip_onboarding') {
                await this.skipOnboarding();
                return;
            }

            if (action === 'prev_step') {
                await this.goOnboardingPrevious();
                return;
            }

            if (action === 'next_step') {
                await this.goOnboardingNext();
                return;
            }
        },
        closeCfdiPreview() {
            this.cfdiPreviewModal.visible = false;
            this.cfdiPreviewModal.uuid = '';
        },
        getMethodBadgeClass(method) {
            if (method === 'PUE') return 'method-primary';
            if (method === 'PPD') return 'method-secondary';
            return 'method-neutral';
        },
        resolveDrilldownType(payload) {
            const seriesName = String(payload?.seriesName || '').toLowerCase();

            if (seriesName.includes('egreso')) {
                return 'expenses';
            }

            if (seriesName.includes('ingreso')) {
                return 'incomes';
            }

            return payload?.seriesIndex === 1 ? 'expenses' : 'incomes';
        },
        async openReviewGraphDrilldown(payload) {
            const month = (payload?.monthIndex ?? 0) + 1;
            const side = this.resolveDrilldownType(payload);
            const sideLabel = side === 'expenses' ? 'Egreso' : 'Ingreso';
            const monthLabel = this.monthLabelsLong[month - 1] || '';
            const reportLabel = this.filters.reportType === 'flujo' ? 'Flujo' : 'Facturado';
            const periodYear = String(this.filters.period || moment().format('YYYY')).split('-')[0];

            this.drilldownModal.visible = true;
            this.drilldownModal.loading = true;
            this.drilldownModal.error = '';
            this.drilldownModal.rows = [];
            this.drilldownModal.reportType = this.filters.reportType;
            this.drilldownModal.title = `${sideLabel} ${reportLabel} - ${monthLabel} ${periodYear}`;
            this.drilldownModal.type = side;

            const detailFilters = {
                ...this.filters,
                month,
                side,
            };

            try {
                const response = await ws.getReviewGraphDetail(this.rfc, detailFilters, { skipLoading: true });

                if (response && response.type == "success") {
                    this.drilldownModal.rows = response.data || [];
                }
                else {
                    this.drilldownModal.error = 'No se pudo consultar el detalle de la grafica.';
                }
            } catch (error) {
                this.drilldownModal.error = 'No se pudo consultar el detalle de la grafica.';
            } finally {
                this.drilldownModal.loading = false;
            }
        }
    },
};
</script>

<style scoped>
.dashboard-v2-shell {
    padding-top: 22px;
    padding-bottom: 20px;
}

.dashboard-v2-charts-col {
    display: flex;
}

.dashboard-v2-charts-row {
    margin-bottom: 1rem;
}

.dashboard-v2-charts-card {
    width: 100%;
    height: 100%;
}

.report-type-wrapper {
    min-width: 170px;
}

.report-type-select {
    width: 100%;
    border: 1px solid rgba(115, 102, 255, 0.35);
    border-radius: 10px;
    background: #ffffff;
    color: var(--theme-deafult);
    padding: 6px 10px;
    font-size: 12px;
    font-weight: 600;
}

.method-badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
}

.method-primary {
    background: rgba(115, 102, 255, 0.16);
    color: var(--theme-deafult);
}

.method-secondary {
    background: rgba(247, 49, 100, 0.16);
    color: var(--theme-secondary);
}

.method-neutral {
    background: rgba(82, 82, 108, 0.12);
    color: rgba(82, 82, 108, 0.9);
}

.amount-income {
    color: var(--theme-deafult);
    font-weight: 600;
}

.amount-expense {
    color: var(--theme-secondary);
    font-weight: 600;
}

.drilldown-table tbody tr,
.drilldown-table tbody tr:nth-of-type(odd),
.drilldown-table tbody tr:nth-of-type(even) {
    background-color: transparent;
}

.drilldown-table.drilldown-income tbody tr {
    background-color: rgba(115, 102, 255, 0.08);
}

.drilldown-table.drilldown-expense tbody tr {
    background-color: rgba(247, 49, 100, 0.08);
}

.modal-title {
    white-space: pre-line;
}

.fx-amount {
    text-decoration: underline dotted;
    text-underline-offset: 2px;
    cursor: pointer;
}

:deep(.popover.fx-popover) {
    border-color: var(--theme-deafult);
}

:deep(.popover.fx-popover .popover-header) {
    background: var(--theme-deafult);
    color: var(--white);
    border-bottom: 1px solid var(--theme-deafult);
}

:deep(.popover.fx-popover .popover-body) {
    color: var(--theme-font-color);
}

.uuid-link-btn {
    background: none;
    border: none;
    padding: 0;
    color: var(--theme-deafult);
    font-size: 10px;
    font-family: monospace;
    text-decoration: underline dotted;
    text-underline-offset: 2px;
    cursor: pointer;
    white-space: nowrap;
}

.uuid-link-btn:hover {
    text-decoration: underline solid;
}

@media (max-width: 768px) {
    .report-type-wrapper {
        width: 100%;
    }
}
</style>
